import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

const GET_TRIAL_STATUS = gql`
  query GetTrialStatus {
    getTrialStatus {
      billingStatus
      currentPlan
    }
  }
`;

export interface PlanLimits {
  canInviteUsers: boolean;
  maxUsers: number;
  currentUsers: number;
  remainingInvites: number;
  planName: string;
  billingStatus: string;
}

export const usePlanLimits = (): PlanLimits => {
  const { data, loading } = useQuery(GET_TRIAL_STATUS);

  if (loading || !data?.getTrialStatus) {
    return {
      canInviteUsers: false,
      maxUsers: 0,
      currentUsers: 0,
      remainingInvites: 0,
      planName: 'Unknown',
      billingStatus: 'unknown',
    };
  }

  const { billingStatus, currentPlan } = data.getTrialStatus;

  // Limits for each plan (aligned with frontend pricing)
  const getPlanLimits = () => {
    switch (currentPlan?.toLowerCase()) {
      case 'trial':
        return { maxUsers: 1, canInvite: billingStatus === 'trial' };
      case 'free':
        // Free plan: singolo utente, nessun invito (upgrade path verso Starter/Pro)
        return { maxUsers: 1, canInvite: false };
      case 'starter':
        return {
          maxUsers: 1,
          canInvite: billingStatus === 'active' || billingStatus === 'trial',
        };
      case 'pro':
        return {
          maxUsers: -1,
          canInvite: billingStatus === 'active' || billingStatus === 'trial',
        }; // unlimited
      case 'advanced':
        return {
          maxUsers: -1,
          canInvite: billingStatus === 'active' || billingStatus === 'trial',
        }; // unlimited
      default:
        return { maxUsers: 1, canInvite: billingStatus === 'trial' }; // Default to trial
    }
  };

  const limits = getPlanLimits();
  const currentUsers = 1; // Per ora hardcoded, in futuro verrà dal backend
  const remainingInvites =
    limits.maxUsers === -1 ? -1 : Math.max(0, limits.maxUsers - currentUsers);

  return {
    canInviteUsers:
      limits.canInvite &&
      (limits.maxUsers === -1 || currentUsers < limits.maxUsers),
    maxUsers: limits.maxUsers,
    currentUsers,
    remainingInvites,
    planName: currentPlan
      ? currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)
      : 'Trial',
    billingStatus,
  };
};
